import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import RightSidebar from './RightSidebar';
import FancyTimeStamp from '../FancyTimeStamp';
import { useUserPresence } from '../../hooks/useUserPresence';
import { useRoomMember } from '../../hooks/useRoomMember';
import { useModal } from '../../context/ModalContext';
import { useMatrixClient } from '../../context/MatrixContext';
import { useI18n } from '../../context/I18nContext';
import { getPresenceColor, getPresenceTitle } from '../../utils/presenceUtils';
import { generateInitialsAvatar } from '../../utils/avatar';
import { POWER_LEVELS, POWER_LEVEL_OPTIONS } from '../../constants/roomSettings';
import logger from '../../utils/logger';
import './RightSidebar.css';
import './ContactDetailsSidebar.css';

const PermissionIcon = (
  <svg width="20" height="16" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_2416_4469)">
      <path d="M13.2844 14.9242C13.2124 14.8855 13.1415 14.845 13.0716 14.8026L12.8273 14.9416C12.6397 15.0499 12.4263 15.1071 12.2101 15.1071C11.8754 15.1071 11.5498 14.9682 11.3048 14.7128C10.7295 14.0915 10.2957 13.3475 10.0479 12.5512C9.88066 11.9927 10.1211 11.411 10.6051 11.1521L10.8557 11.0078C10.8536 10.9267 10.8536 10.8456 10.8557 10.7647L10.6164 10.6268C10.2321 10.4117 10.0102 10.0188 10.0027 9.59451C9.55 9.47217 9.075 9.40104 8.58438 9.40104H5.41563C2.42531 9.40104 0 11.8039 0 14.7633C0 15.354 0.485 15.8333 1.08312 15.8333H12.9156C13.0946 15.8333 13.2562 15.7799 13.405 15.7038C13.334 15.5497 13.2844 15.3853 13.2844 15.2072V14.9242ZM7 7.91667C9.20938 7.91667 11 6.14438 11 3.95833C11 1.77228 9.20938 0 7 0C4.79063 0 3 1.77228 3 3.95833C3 6.14438 4.79063 7.91667 7 7.91667ZM19.0781 11.5441C19.1602 11.1112 19.1602 10.6628 19.0781 10.2298L19.8828 9.76595C19.9766 9.7157 20.0195 9.60746 19.9883 9.50309C19.7773 8.83822 19.418 8.23117 18.9492 7.72865C18.8789 7.65133 18.7617 7.63201 18.668 7.68612L17.8633 8.14628C17.5233 7.86023 17.1327 7.63603 16.7108 7.48914V6.56527C16.7108 6.4609 16.6327 6.36812 16.5311 6.3488C15.8358 6.19417 15.1248 6.19804 14.4608 6.3488C14.3592 6.36812 14.285 6.4609 14.285 6.56527V7.48929C13.8631 7.63618 13.4725 7.86038 13.1325 8.14643L12.325 7.68473C12.2352 7.63062 12.118 7.64994 12.0437 7.72725C11.575 8.22978 11.2156 8.83682 11.0084 9.5017C10.9772 9.60607 11.0202 9.71431 11.11 9.76456L11.9147 10.2284C11.8366 10.6614 11.8366 11.1098 11.9147 11.5427L11.11 12.0066C11.0162 12.0568 10.9772 12.1651 11.0084 12.2694C11.2155 12.9343 11.575 13.5373 12.0437 14.0439C12.118 14.1212 12.2312 14.1405 12.325 14.0864L13.1337 13.6263C13.4738 13.9123 13.8644 14.1365 14.2863 14.2834V15.2074C14.2863 15.3118 14.3605 15.4046 14.462 15.4239C15.1614 15.5785 15.8683 15.5746 16.5323 15.4239C16.6339 15.4046 16.712 15.3118 16.712 15.2074V14.2834C17.1339 14.1365 17.5245 13.9123 17.8645 13.6263L18.6692 14.0864C18.7591 14.1405 18.8802 14.1212 18.9505 14.0439C19.4192 13.5414 19.7786 12.9343 19.9895 12.2694C20.0208 12.1651 19.9778 12.0568 19.8841 12.0066L19.0781 11.5441ZM15.5 12.3853C14.6641 12.3853 13.9844 11.7126 13.9844 10.8854C13.9844 10.0582 14.6641 9.38558 15.5 9.38558C16.3359 9.38558 17.0156 10.0582 17.0156 10.8854C17.0156 11.7126 16.3375 12.3853 15.5 12.3853Z" fill="#4A4A4A"/>
    </g>
    <defs>
      <clipPath id="clip0_2416_4469">
        <rect width="20" height="15.8333" fill="white"/>
      </clipPath>
    </defs>
  </svg>
);

const RemoveIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.9903 7.00451C12.8279 7.00451 13.6466 7.25288 14.343 7.71821C15.0395 8.18354 15.5822 8.84493 15.9028 9.61875C16.2233 10.3926 16.3072 11.244 16.1438 12.0655C15.9804 12.887 15.577 13.6416 14.9848 14.2338C14.3925 14.8261 13.6379 15.2294 12.8165 15.3928C11.995 15.5562 11.1435 15.4724 10.3697 15.1518C9.59587 14.8313 8.93448 14.2885 8.46915 13.5921C8.00382 12.8957 7.75545 12.0769 7.75545 11.2393C7.75545 10.1162 8.20162 9.03905 8.99581 8.24486C9.78999 7.45068 10.8671 7.00451 11.9903 7.00451ZM16.6101 5.84955H21.2299C21.4342 5.84955 21.63 5.76843 21.7744 5.62404C21.9188 5.47964 21.9999 5.28379 21.9999 5.07958C21.9999 4.87538 21.9188 4.67953 21.7744 4.53513C21.63 4.39074 21.4342 4.30961 21.2299 4.30961H16.6101C16.4059 4.30961 16.2101 4.39074 16.0657 4.53513C15.9213 4.67953 15.8401 4.87538 15.8401 5.07958C15.8401 5.28379 15.9213 5.47964 16.0657 5.62404C16.2101 5.76843 16.4059 5.84955 16.6101 5.84955ZM20.4475 8.20374C20.255 8.27192 20.0975 8.41374 20.0096 8.59802C19.9217 8.7823 19.9106 8.99395 19.9787 9.18641C20.4821 10.6138 20.5937 12.1499 20.302 13.6351C20.0104 15.1202 19.3261 16.5 18.3204 17.6311C17.7601 16.8193 17.0487 16.1229 16.2251 15.5801C16.1548 15.5335 16.0712 15.5112 15.987 15.5168C15.9029 15.5224 15.8229 15.5554 15.7593 15.6109C14.7132 16.516 13.3761 17.0142 11.9927 17.0142C10.6093 17.0142 9.27222 16.516 8.2261 15.6109C8.16217 15.5543 8.08124 15.5205 7.99604 15.5149C7.91084 15.5094 7.82621 15.5323 7.75545 15.5801C6.93097 16.1225 6.21888 16.8189 5.65824 17.6311C4.57504 16.4104 3.86754 14.9029 3.62084 13.2897C3.37413 11.6765 3.59874 10.0264 4.26763 8.53781C4.93653 7.04924 6.02122 5.78562 7.39127 4.89892C8.76131 4.01221 10.3583 3.5402 11.9903 3.53964C12.4637 3.53957 12.9363 3.57884 13.4032 3.65706C13.6037 3.6887 13.8086 3.63989 13.9732 3.52125C14.1379 3.40261 14.2491 3.22374 14.2826 3.02355C14.3161 2.82336 14.2692 2.61805 14.1521 2.45226C14.035 2.28648 13.8572 2.17364 13.6573 2.1383C11.4939 1.77474 9.27105 2.13264 7.33113 3.15684C5.39122 4.18105 3.84191 5.81473 2.92191 7.80618C2.00192 9.79763 1.76229 12.0363 2.23994 14.1774C2.71759 16.3185 3.88603 18.243 5.56522 19.6546C7.24441 21.0662 9.34118 21.8865 11.5325 21.9891C13.7238 22.0916 15.888 21.4708 17.6917 20.2222C19.4954 18.9736 20.8385 17.1666 21.514 15.0795C22.1896 12.9924 22.1601 10.7411 21.4301 8.67246C21.362 8.48001 21.2201 8.32252 21.0359 8.23462C20.8516 8.14673 20.6399 8.13562 20.4475 8.20374Z" fill="#4A4A4A"/>
  </svg>
);

const IgnoreIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM4 12C4 7.58 7.58 4 12 4C13.85 4 15.55 4.63 16.9 5.69L5.69 16.9C4.63 15.55 4 13.85 4 12ZM12 20C10.15 20 8.45 19.37 7.1 18.31L18.31 7.1C19.37 8.45 20 10.15 20 12C20 16.42 16.42 20 12 20Z" fill="#4A4A4A"/>
  </svg>
);

const BanIcon = (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 2C6.47 2 2 6.47 2 12C2 17.53 6.47 22 12 22C17.53 22 22 17.53 22 12C22 6.47 17.53 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 9.97 4.68 8.1 5.82 6.61L17.39 18.18C15.9 19.32 14.03 20 12 20ZM18.18 17.39L6.61 5.82C8.1 4.68 9.97 4 12 4C16.41 4 20 7.59 20 12C20 14.03 19.32 15.9 18.18 17.39Z" fill="#DC2626"/>
  </svg>
);

const ContactDetailsSidebar = ({
  contact = null,
  roomId = null,
  onClose = () => {},
}) => {
  const { t, lang } = useI18n();
  const client = useMatrixClient();
  const { getPresence, isOnline } = useUserPresence();
  const { showError, showConfirm } = useModal();
  const [isProcessing, setIsProcessing] = useState(false);
  const [customPowerLevel, setCustomPowerLevel] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isIgnored, setIsIgnored] = useState(false);
  
  const userId = contact?.user?.userId || contact?.id;
  const currentUserId = client?.getUserId();
  const isOwnProfile = userId === currentUserId;
  const displayName = contact?.user?.displayName || contact?.name || userId || '';
  const avatarUrl = contact?.avatar || generateInitialsAvatar(displayName);
  const presenceObj = userId ? getPresence(userId) : null;
  const online = userId ? isOnline(userId) : false;

  const {
    powerLevel,
    canKick,
    canBan,
    canSetPowerLevel,
    setUserPowerLevel,
    kickUser,
    banUser,
    ignoreUser,
    unignoreUser,
  } = useRoomMember(roomId, userId);
  
  // Check if user is already ignored
  useEffect(() => {
    if (client && userId && !isOwnProfile) {
      try {
        const ignoredUsers = client.getIgnoredUsers();
        setIsIgnored(ignoredUsers.includes(userId));
      } catch (error) {
        logger.error('Failed to check ignored users list:', error);
      }
    }
  }, [client, userId, isOwnProfile]);

  // Initialize custom power level for non-preset values
  useEffect(() => {
    if (powerLevel !== null) {
      const isCustomValue = !POWER_LEVEL_OPTIONS.find(opt => opt.value === powerLevel && opt.value !== POWER_LEVELS.CUSTOM);
      if (isCustomValue) {
        setCustomPowerLevel(powerLevel.toString());
      }
    }
  }, [powerLevel]);
  
  if (!contact || !roomId) return null;

  const handlePowerLevelChange = async (e) => {
    const value = parseInt(e.target.value, 10);
    
    if (value === POWER_LEVELS.CUSTOM) {
      setShowCustomInput(true);
      setCustomPowerLevel(powerLevel?.toString() || '');
    } else {
      setShowCustomInput(false);
      setIsProcessing(true);
      try {
        await setUserPowerLevel(value);
      } catch (error) {
        logger.error('Failed to set power level:', error);
        showError(t('ChatDetailsController.failedUpdatePermissionLevel'), error.message);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  const handleCustomPowerLevelApply = async () => {
    const value = parseInt(customPowerLevel, 10);
    if (isNaN(value) || value < 0 || value > 100) {
      showError(t('ChatDetailsController.invalidPowerLevel'), t('ChatDetailsController.powerLevelRangeError'));
      return;
    }

    setIsProcessing(true);
    try {
      await setUserPowerLevel(value);
      setShowCustomInput(false);
    } catch (error) {
      logger.error('Failed to set custom power level:', error);
      showError(t('ChatDetailsController.failedUpdatePermissionLevel'), error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCustomKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCustomPowerLevelApply();
    } else if (e.key === 'Escape') {
      setShowCustomInput(false);
      setCustomPowerLevel('');
    }
  };

  const handleCustomValueClick = () => {
    if (canSetPowerLevel && !isProcessing) {
      setShowCustomInput(true);
      setCustomPowerLevel(powerLevel.toString());
    }
  };

  const handleRemove = async () => {
    logger.info('handleRemove called - starting removal process');
    logger.info('Current state:', { canKick, userId, roomId, displayName });
    
    if (!canKick) {
      logger.warn('Cannot kick - insufficient permissions:', { canKick, userId, roomId });
      showError(t('ChatDetailsController.cannotRemoveUser'), t('ChatDetailsController.noPermissionToKick'));
      return;
    }

    logger.info('About to show confirmation dialog');
    
    // Wrap showConfirm in a promise since it uses callbacks
    const confirmed = await new Promise((resolve) => {
      showConfirm(
        t('ChatDetailsController.kickUserTitle'),
        t('ChatDetailsController.kickUserMessage', { displayName }),
        () => resolve(true),  // onConfirm
        () => resolve(false),  // onCancel
      );
    });

    logger.info('User confirmation result:', confirmed);

    if (!confirmed) return;

    setIsProcessing(true);
    try {
      logger.info('Attempting to kick user:', { userId, roomId, canKick });
      await kickUser('Removed from room');
      logger.info('Successfully kicked user:', userId);
      onClose();
    } catch (error) {
      logger.error('Failed to remove user:', error);
      showError(t('ChatDetailsController.failedRemoveUser'), error.message || t('ChatDetailsController.unknownError'));
      setIsProcessing(false);
    }
  };

  const handleIgnoreToggle = async () => {
    const action = isIgnored ? 'unignore' : 'ignore';
    logger.info(`${action} user action triggered for:`, userId);
    logger.info(`About to show ${action} confirmation dialog`);
    
    // Wrap showConfirm in a promise since it uses callbacks
    const confirmed = await new Promise((resolve) => {
      showConfirm(
        isIgnored ? t('ChatDetailsController.unignoreUserTitle') : t('ChatDetailsController.ignoreUserTitle'),
        isIgnored 
          ? t('ChatDetailsController.unignoreUserMessage', { displayName })
          : t('ChatDetailsController.ignoreUserMessage', { displayName }),
        () => resolve(true),  // onConfirm
        () => resolve(false),  // onCancel
      );
    });

    logger.info(`User ${action} confirmation result:`, confirmed);

    if (!confirmed) return;

    setIsProcessing(true);
    try {
      if (isIgnored) {
        logger.info('Attempting to unignore user:', userId);
        await unignoreUser();
        logger.info('Successfully unignored user:', userId);
        setIsIgnored(false);
      } else {
        logger.info('Attempting to ignore user:', userId);
        await ignoreUser();
        logger.info('Successfully ignored user:', userId);
        setIsIgnored(true);
      }
    } catch (error) {
      logger.error(`Failed to ${action} user:`, error);
      showError(
        isIgnored ? t('ChatDetailsController.failedUnignoreUser') : t('ChatDetailsController.failedIgnoreUser'),
        error.message || t('ChatDetailsController.unknownError'),
      );
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBan = async () => {
    logger.info('handleBan called - starting ban process');
    logger.info('Current state:', { canBan, userId, roomId, displayName });
    
    if (!canBan) {
      logger.warn('Cannot ban - insufficient permissions:', { canBan, userId, roomId });
      showError(t('ChatDetailsController.cannotBanUser'), t('ChatDetailsController.noPermissionToBan'));
      return;
    }

    logger.info('About to show ban confirmation dialog');
    
    // Wrap showConfirm in a promise since it uses callbacks
    const confirmed = await new Promise((resolve) => {
      showConfirm(
        t('ChatDetailsController.banUserTitle'),
        t('ChatDetailsController.banUserMessage', { displayName }),
        () => resolve(true),  // onConfirm
        () => resolve(false),  // onCancel
      );
    });

    logger.info('User ban confirmation result:', confirmed);

    if (!confirmed) return;

    setIsProcessing(true);
    try {
      logger.info('Attempting to ban user:', { userId, roomId, canBan });
      await banUser('Banned from room');
      logger.info('Successfully banned user:', userId);
      onClose();
    } catch (error) {
      logger.error('Failed to ban user:', error);
      showError(t('ChatDetailsController.failedBanUser'), error.message || t('ChatDetailsController.unknownError'));
      setIsProcessing(false);
    }
  };

  return (
    <RightSidebar
      onClose={onClose}
      title={t('ChatDetailsController.contactDetails')}
    >
      <div className="contact-details-sidebar">
        <div className="contact-details-avatar">
          <img
            src={avatarUrl}
            alt={displayName.charAt(0).toUpperCase()}
            className="contact-details-avatar-img"
            onError={e => { e.target.src = generateInitialsAvatar(displayName); }}
          />
          {userId && (
            <div
              className="contact-details-presence-indicator"
              style={{ backgroundColor: getPresenceColor(presenceObj, online) }}
              title={getPresenceTitle(presenceObj, online)}
            />
          )}
        </div>
        
        <div className="contact-details-name">{displayName}</div>
        <div className="contact-details-user-id">{userId}</div>
        {presenceObj?.lastActiveAgo !== null && presenceObj?.lastActiveAgo !== undefined && (
          <div className="contact-details-last-active">
            {t('ChatDetailsController.lastActive')} <FancyTimeStamp 
              unixTime={(Date.now() - (presenceObj?.lastActiveAgo || 0)) / 1000}
              showNow={true}
              displayTitle={false}
              currentLanguage={lang}
            />
          </div>
        )}

        <div className="contact-details-settings-list">
          {canSetPowerLevel && (
            <div className="contact-details-row">
              <div className="contact-details-row-icon">
                {PermissionIcon}
              </div>
              <span className="contact-details-row-label">{t('ChatDetailsController.permissionLevel')}</span>
              <div className="contact-details-row-control">
                <select
                  id="contact-details-permission-select"
                  className="contact-details-select"
                  value={
                    showCustomInput 
                      ? POWER_LEVELS.CUSTOM 
                      : (POWER_LEVEL_OPTIONS.find(opt => opt.value === powerLevel && opt.value !== POWER_LEVELS.CUSTOM) 
                          ? powerLevel 
                          : POWER_LEVELS.CUSTOM)
                  }
                  onChange={handlePowerLevelChange}
                  disabled={isProcessing}
                >
                  {POWER_LEVEL_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {t(option.label)}
                    </option>
                  ))}
                </select>
                {showCustomInput && (
                  <div className="contact-details-custom-input-container">
                    <input
                      id="contact-details-custom-power-level-input"
                      type="number"
                      min="0"
                      className="contact-details-custom-input"
                      value={customPowerLevel}
                      onChange={(e) => setCustomPowerLevel(e.target.value)}
                      onKeyDown={handleCustomKeyPress}
                      onBlur={handleCustomPowerLevelApply}
                      placeholder={t('ChatDetailsController.customLevelPlaceholder')}
                      disabled={isProcessing}
                      autoFocus
                    />
                  </div>
                )}
                {!showCustomInput && powerLevel !== null && !POWER_LEVEL_OPTIONS.find(opt => opt.value === powerLevel && opt.value !== POWER_LEVELS.CUSTOM) && (
                  <span 
                    className={`contact-details-custom-value ${canSetPowerLevel ? 'contact-details-custom-value-clickable' : ''}`}
                    onClick={handleCustomValueClick}
                    title={canSetPowerLevel ? t('ChatDetailsController.customLevelClickToEdit') : ''}
                  >
                    ({powerLevel})
                  </span>
                )}
              </div>
            </div>
          )}

          {!isOwnProfile && (
            <>
              <div
                className="contact-details-row contact-details-row-clickable"
                onClick={isProcessing ? undefined : handleIgnoreToggle}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isProcessing) {
                    e.preventDefault();
                    handleIgnoreToggle();
                  }
                }}
              >
                <div className="contact-details-row-icon">
                  {IgnoreIcon}
                </div>
                <span className="contact-details-row-label">
                  {isIgnored ? t('ChatDetailsController.unignoreUser') : t('ChatDetailsController.ignoreUser')}
                </span>
              </div>
              {canKick && (
                <div
                  className="contact-details-row contact-details-row-clickable"
                  onClick={isProcessing ? undefined : handleRemove}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !isProcessing) {
                      e.preventDefault();
                      handleRemove();
                    }
                  }}
                >
                  <div className="contact-details-row-icon contact-details-row-icon-danger">
                    {RemoveIcon}
                  </div>
                  <span className="contact-details-row-label">{t('ChatDetailsController.kick')}</span>
                </div>
              )}
              {canBan && (
                <div
                  className="contact-details-row contact-details-row-clickable"
                  onClick={isProcessing ? undefined : handleBan}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && !isProcessing) {
                      e.preventDefault();
                      handleBan();
                    }
                  }}
                >
                  <div className="contact-details-row-icon contact-details-row-icon-danger">
                    {BanIcon}
                  </div>
                  <span className="contact-details-row-label">{t('ChatDetailsController.ban')}</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </RightSidebar>
  );
};

ContactDetailsSidebar.propTypes = {
  contact: PropTypes.object,
  roomId: PropTypes.string,
  onClose: PropTypes.func,
};

export default ContactDetailsSidebar;
